import { ReactNode, useContext } from 'react';
import { TranslationContext } from 'contexts/translation-context';
import styled from 'styled-components';
import Link from 'components/common/link/link';
import { PRICE_LIST_TERMS_LINK, PRIVACY_POLICY_LINK, RAPNET_TERMS_LINK } from 'constants/urls';
import RapaportLogoWhite from 'assets/rapaport-logo-white.svg';

const PageWrapper = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.siteGrey};
`;

const Header = styled.div`
  top: 0px;
  width: 100%;
  height: 64px;
  background-color: ${props => props.theme.colors.black};
  border-bottom: 1px solid rgba(0, 0, 0, 0.8);
  position: fixed;
  z-index: 10;

  @media (max-width: 999px) {
    height: 56px;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1152px;
  height: 100%;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  @media (max-width: 999px) {
    padding: 0 20px;
  }
`;

const HeaderLogoLink = styled.a`
  display: inline-flex;
  align-items: center;
`;

const HeaderLogo = styled.img`
  height: 32px;
  width: auto;

  @media (max-width: 999px) {
    height: 28px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;

  @media (max-width: 999px) {
    gap: 10px;
  }
`;

const ChatWithUs = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.interSansMedium};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: ${props => props.theme.colors.white};
    opacity: 0.85;
  }

  @media (max-width: 999px) {
    font-size: 11px;
  }
`;

const ChatBubbleIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 14px;
  border: 1px solid ${props => props.theme.colors.white};
  border-radius: 4px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 4px;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid ${props => props.theme.colors.white};
  }
`;

const LoginButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.white};
  background-color: transparent;
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 12px;
  font-weight: 500;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.black};
  }
`;

const Footer = styled.div`
  width: 100%;
  background-color: #595959;
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  z-index: 3;
  margin-top: auto;

  @media (max-width: 999px) {
    flex-direction: column;
    padding: 12px 16px;
    gap: 6px;
  }
`;

const FooterText = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 14px;

  @media (max-width: 999px) {
    font-size: 10px;
    text-align: center;
  }
`;

const FooterLinks = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 14px;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    color: ${props => props.theme.colors.white};
    opacity: 0.8;
    cursor: pointer;
  }

  @media (max-width: 999px) {
    font-size: 10px;
  }
`;

const TextWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: ${props => props.theme.fonts.interSans};
  flex-wrap: wrap;
  justify-content: center;
  gap: 0;

  @media (max-width: 999px) {
    text-align: center;
  }
`;

const Divider = styled.span`
  font-size: 14px;
  color: ${props => props.theme.colors.white};
  margin: 0 8px;

  @media (max-width: 999px) {
    display: none;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-top: 64px;

  @media (max-width: 999px) {
    padding-top: 56px;
    padding-bottom: 16px;
  }
`;

type HeaderFooterProps = { children: ReactNode; isRapnet?: boolean };
export const HeaderFooter = ({ children, isRapnet }: HeaderFooterProps) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const termsAndConditionsLink = isRapnet ? RAPNET_TERMS_LINK : PRICE_LIST_TERMS_LINK;
  const currentYear = new Date().getFullYear();
  const loginUrl =
    (typeof CONFIG !== 'undefined' && (CONFIG as any).RAPNET_LOGIN_URL) ||
    'https://trade.rapaport.com/login';

  return (
    <PageWrapper>
      <Header>
        <HeaderInner>
          <HeaderLogoLink href="https://www.rapaport.com" target="_blank" rel="noopener noreferrer">
            <HeaderLogo src={RapaportLogoWhite} alt="Rapaport" />
          </HeaderLogoLink>
          <HeaderRight>
            <ChatWithUs
              onClick={e => {
                e.preventDefault();
                const intercom = (window as any).Intercom;
                if (typeof intercom === 'function') {
                  intercom('show');
                }
              }}
            >
              <ChatBubbleIcon />
              {TRANSLATION_TEXT.chat_with_us || 'Chat with us'}
            </ChatWithUs>
            <LoginButton href={loginUrl} target="_blank" rel="noopener noreferrer">
              Log In
            </LoginButton>
          </HeaderRight>
        </HeaderInner>
      </Header>
      <Container>{children}</Container>
      <Footer>
        <TextWrapper>
          <FooterText>
            All Content © Copyright 1982-{currentYear} by Rapaport USA Inc. All rights reserved.
          </FooterText>
          <Divider>|</Divider>
          <FooterLinks href={termsAndConditionsLink} external newTab>
            {TRANSLATION_TEXT.terms_and_conditions}
          </FooterLinks>
          <FooterText>&nbsp;{TRANSLATION_TEXT.and || 'and'}&nbsp; </FooterText>
          <FooterLinks href={PRIVACY_POLICY_LINK} external newTab>
            {TRANSLATION_TEXT.privacy_policy}.
          </FooterLinks>
          <Divider>|</Divider>
          <FooterText>{`Rapaport © ${currentYear}`}</FooterText>
        </TextWrapper>
      </Footer>
    </PageWrapper>
  );
};
